import { useEffect } from 'react';
import {
  generatePath,
  Outlet,
  useMatch,
  useNavigate,
  NavLink,
  useParams,
} from 'react-router-dom';
import { Tabs, IconLoader, PermissionDenied, PermissionEnum } from '@ui/src';
import { useLazyGetEmployeesDictionariesQuery } from '@administration/store/dictionaries/service';
import { useAppDispatch, useAppSelector } from '@store';
import { clearAutoSettingsListState } from './store/slices/list';

import ROUTES from '../../routes';

import cn from './auto-settings.module.css';

const AutoSettings = (): React.JSX.Element => {
  const {
    exchangeDictionaries: { currencies, activePermissions },
    administrationDictionaries: { users },
  } = useAppSelector((state) => state);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const match = useMatch(ROUTES.AUTO_SETTINGS);
  const [getEmployeeDictionaries] = useLazyGetEmployeesDictionariesQuery();

  const { currency } = useParams();

  useEffect(() => {
    if (
      !users &&
      activePermissions &&
      activePermissions?.includes(PermissionEnum.USERS_LIST)
    ) {
      getEmployeeDictionaries();
    }
  }, [users, activePermissions]);

  useEffect(() => {
    if (
      currencies &&
      currencies.length &&
      match &&
      activePermissions &&
      activePermissions.includes(PermissionEnum.SETTINGS_AUTO_EXCHANGE_LIST)
    ) {
      navigate(
        generatePath(ROUTES.AUTO_SETTINGS_CURRENCY, { currency: 'all' }),
      );
    }
  }, [currencies, activePermissions, match]);

  useEffect(
    () => () => {
      dispatch(clearAutoSettingsListState());
    },
    [],
  );

  useEffect(() => {
    let title = 'Auto FX settings';

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, []);

  let content = (
    <div className={cn.autoSettingsLoader}>
      <IconLoader />
    </div>
  );

  if (activePermissions && currencies && currencies.length) {
    if (
      activePermissions.includes(PermissionEnum.SETTINGS_AUTO_EXCHANGE_LIST)
    ) {
      content = (
        <div className={cn.autoSettings}>
          <div className={cn.autoSettingsTopWrapper}>
            <div className={cn.autoSettingsTitle}>Auto FX settings</div>
            <div className={cn.autoSettingsTitleDescription}>
              Auto-exchange activity by currency pairs and its settings
            </div>
            {currencies && currencies?.length && (
              <div className={cn.autoSettingsControlRow}>
                <Tabs
                  tabs={[
                    {
                      name: 'All',
                      path: generatePath(ROUTES.AUTO_SETTINGS_CURRENCY, {
                        currency: 'all',
                      }),
                    },
                    ...currencies.slice(0, 7).map((item) => {
                      const url = generatePath(ROUTES.AUTO_SETTINGS_CURRENCY, {
                        currency: item.id,
                      });

                      return {
                        name: item.name,
                        path: url,
                      };
                    }),
                  ]}
                  hiddenRoutes={currencies?.slice(7)?.map((item) => {
                    const url = generatePath(ROUTES.AUTO_SETTINGS_CURRENCY, {
                      currency: item.id,
                    });

                    return {
                      name: item.name,
                      path: url,
                    };
                  })}
                  wrapperClassName={cn.tabsWrapper}
                  itemClassName={cn.tabsItem}
                  enableRouting
                />
                <NavLink
                  to={generatePath(ROUTES.AUTO_SETTINGS_GENERAL, {
                    currency: currency || '',
                  })}
                  className={cn.generalSettings}
                >
                  General settings
                </NavLink>
              </div>
            )}
          </div>
          <Outlet />
        </div>
      );
    } else {
      content = <PermissionDenied />;
    }
  }

  return content;
};

export default AutoSettings;
